<script lang="ts">
  import { readable, Readable } from "svelte/store";
  import { form as svelteForm, field, Validator } from "svelte-forms2";
  import { required } from "svelte-forms2/validators";
  import {
    containsNumber,
    containsSpecialChar,
    minLength,
    phoneValidator,
  } from "@shared/svelte/Validators/User";
  import { validateEmailApi } from "@shared/svelte/Validators/Email";
  import { RetailerEntity } from "@shared/services/event_tracking/entities";

  // components
  import EmailInput from "@shared/svelte/Components/EmailInput.svelte";
  import FormInputError from "@shared/svelte/Components/FormInputError.svelte";
  import MaskedPhoneInput from "@shared/svelte/Components/MaskedPhoneInput.svelte";
  import PasswordInput from "@shared/svelte/Components/PasswordInput.svelte";
  import TextInput from "@shared/svelte/Components/TextInput.svelte";

  // services
  import { EventTracking } from "@shared/services/event_tracking/events";
  import * as RetailerUserSignup from "@onboarding2/services/retailer_user_signup";
  import { blur, change } from "@shared/services/svelte_form_input_service";

  // stores
  import SignupUserStore from "@onboarding2/svelte/Stores/SignupUserStore";

  // types
  import type { UserSignUp } from "@shared/types/User";

  export let refCode: string = "";

  let user: UserSignUp = {};

  const { trackClick, trackEvent } = EventTracking;

  const formErrorsMap = {
    first_name: {
      required: "First Name is required",
    },
    last_name: {
      required: "Last Name is required",
    },
    password: {
      required: "Password is required",
      contains_number: "Password requires at least one number",
      contains_special_char: "Password requires at least one special character",
      custom_min: "Password length is invalid",
    },
    phone: { required: "Phone number is required", invalid_phone: "Phone number is invalid" },
    email: { required: "Email is required", not_an_email: "Email format is invalid" },
  };

  const labelClasses: string = "fizz-stack-4";
  const params: Readable<any> = readable(window["stateService"].params);
  const emailParam = new URLSearchParams(window.location.search).get("email");
  const passwordValidations: Validator[] = [
    required(),
    minLength(),
    containsNumber(),
    containsSpecialChar(),
  ];
  const signupUserStore = SignupUserStore(user);
  const validationOptions = { validateOnChange: false, stopAtFirstError: true };

  const firstName = field("first_name", "", [required()], validationOptions);
  const lastName = field("last_name", "", [required()], validationOptions);
  const email = field("email", emailParam || "", [required(), validateEmailApi()], {
    validateOnChange: false,
    stopAtFirstError: true,
  });
  const password = field("password", "", passwordValidations, validationOptions);
  const phone = field("phone", "", [required(), phoneValidator()], validationOptions);
  const referralCode = field("referral_code_used", refCode, [], validationOptions);
  const userForm = svelteForm(firstName, lastName, email, password, phone, referralCode);

  let showReferralCodeInput: boolean = false;
  let submitting = false;

  const showReferral = (_e: Event): void => {
    showReferralCodeInput = true;
    trackClick("create_account_referral_button");
  };

  const runSubmitUserWrapper = async (_e: Event): Promise<void> => {
    trackClick("create_account_create_button");

    await userForm.validate();

    if (!$userForm.valid) {
      return RetailerUserSignup.trackErrorImpressions(userFriendlyErrors($userForm.errors));
    }

    submitting = true;
    user = {
      ...user,
      ...$userForm.summary,
      phone: $phone.value.replace(/\D/g, ""),
    };

    signupUserStore
      .save(user)
      .then((res: UserSignUp) => {
        if (res.retailers?.length && res.invitation_status === "accepted") {
          trackEvent("create_invited_account_success", [
            RetailerEntity.fromRetailer(res.retailers[0]),
          ]);
          let referralData = {
            retailer: res.retailers[0],
            referralDistributorName: res.inviter_company_name,
            referralName: res.inviter_first_name,
          };

          window["stateService"].go("layout.signedIn.referralRetailerConfirmation", {
            referralData,
            ...$params,
          });
        } else {
          trackEvent("create_non_invited_account_success");
          window["stateService"].go("layout.signedIn.details", $params);
        }
      })
      .catch(error => RetailerUserSignup.setErrors(signupUserStore, error.errors.messages))
      .finally(() => (submitting = false));
  };

  const userFriendlyErrors = (formErrors: string[]): string[] => {
    return formErrors.map(errorName => {
      const [key, error] = errorName.split(".");

      return formErrorsMap[key][error];
    });
  };

</script>

<div data-grid-area="main" style="background: #F5F7F7">
  <form
    class="fizz-card fizz-flow-16 fizz-radius-medium fizz-elevation-1"
    on:submit|preventDefault={runSubmitUserWrapper}
  >
    <h2 class="fizz-heading-2 fizz-text-center fizz-stack-16">Sign up for Provi</h2>
    <p class="fizz-text-center fizz-text-medium fizz-stack-16" />
    <div class="fizz-flex-row-responsive fizz-align-content-start">
      <div class="fizz-flex-item fizz-full-width">
        <TextInput
          classes={"fizz-flex-item fizz-full-width"}
          name="first_name"
          label="First Name"
          field={firstName}
          {labelClasses}
          on:blur={blur}
          on:change={change}
          on:click={() => trackClick("create_account_first_name_field")}
        >
          {#each Object.entries(formErrorsMap.first_name) as [key, error]}
            <FormInputError form={userForm} errorName="first_name.{key}">
              {error}
            </FormInputError>
          {/each}
        </TextInput>
      </div>
      <div class="fizz-flex-item fizz-full-width">
        <TextInput
          classes={"fizz-flex-item fizz-full-width"}
          name="last_name"
          label="Last Name"
          field={lastName}
          {labelClasses}
          on:blur={blur}
          on:change={change}
          on:click={() => trackClick("create_account_last_name_field")}
        >
          {#each Object.entries(formErrorsMap.last_name) as [key, error]}
            <FormInputError form={userForm} errorName="last_name.{key}">
              {error}
            </FormInputError>
          {/each}
        </TextInput>
      </div>
    </div>
    <div>
      <EmailInput
        name="email"
        label="Email"
        field={email}
        on:blur={blur}
        on:change={change}
        on:click={() => trackClick("create_account_email_field")}
      >
        {#each Object.entries(formErrorsMap.email) as [key, error]}
          <FormInputError form={userForm} errorName="email.{key}">
            {error}
          </FormInputError>
        {/each}
      </EmailInput>
      <PasswordInput
        {labelClasses}
        classes={"fizz-full-width fizz-flow-4 fizz-inline-block"}
        field={password}
        label="Password"
        name="password"
        showPasswordToggle
        on:blur={blur}
        on:change={change}
        on:click={() => trackClick("create_account_password_field")}
      >
        {#each Object.entries(formErrorsMap.password) as [key, error]}
          <FormInputError form={userForm} errorName="password.{key}">
            {error}
          </FormInputError>
        {/each}
        <p class="fizz-text-subdued">
          Password must be at least 8 characters, contain letters, numbers and special characters
          (!, @, #, $, %, ^, &, *, _)
        </p>
      </PasswordInput>
    </div>
    <MaskedPhoneInput
      classes={"fizz-full-width fizz-flow-4 fizz-inline-block"}
      field={phone}
      label="Mobile Phone Number"
      name="phone"
      tooltip="This will be used for SMS order notifications"
      {labelClasses}
      on:blur={blur}
      on:change={change}
      on:click={() => trackClick("create_account_phone_field")}
    >
      {#each Object.entries(formErrorsMap.phone) as [key, error]}
        <FormInputError form={userForm} errorName="phone.{key}">
          {error}
        </FormInputError>
      {/each}
    </MaskedPhoneInput>
    <div class="fizz-stack-32">
      {#if showReferralCodeInput}
        <TextInput
          classes={"fizz-full-width fizz-flow-4 fizz-inline-block"}
          label="Referral Code"
          name="referral_code_used"
          field={referralCode}
          {labelClasses}
          on:click={() => trackClick("create_account_referral_field")}
        >
          <p class="fizz-text-subdued">Optional</p>
        </TextInput>
      {:else}
        <a href={"#"} on:click|preventDefault={showReferral}>Did someone refer you?</a>
      {/if}
    </div>
    <button
      type="submit"
      class="fizz-button-secondary fizz-full-width fizz-button-large"
      disabled={submitting}
    >
      Continue
    </button>
    <p class="fizz-text-center fizz-text-medium fizz-stack-24">
      By creating your Provi account, you agree to our <a href="/terms" target="_blank">
        Terms of Service
      </a>
      and <a href="/privacy" target="_blank">Privacy Policy</a>
    </p>
  </form>
</div>
